import React, { useEffect, useState } from 'react';
import {
  FormControl,
  Typography,
  Grid,
  ButtonProps,
  Stack,
  Autocomplete,
  TextFieldProps,
  Fade,
  FormHelperText,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { getOnboardingUserIdFromLocalStorage } from '../../../helper';
import { getCurriculumV2State } from '../../../store/V2/CurriculumSlice';
import {
  getOnboardingState,
  saveOnboarding,
} from '../../../store/V2/OnboardingSlice';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../components/Inputs';
import CustomStepper from './components/CustomStepper';
import DarkLoadingButton from '../../../components/V2/Buttons';

export const backButtonProps: ButtonProps = {
  size: 'large',
  variant: 'text',
  startIcon: <ArrowBack />,
  sx: {
    position: 'absolute',
    left: '50px',
    top: '50px',
    textDecoration: 'underline',
    color: TP_COLORS.gray.darken,
    borderRadius: '30px',
  },
};

const textFieldProps: TextFieldProps = {
  sx: {
    input: {
      color: '#647488',
    },
    '& .MuiInputBase-root': {
      height: '52px',
      fontSize: '14px',
      fontWeight: '600',
    },
  },
};

type Step1Props = {
  onNext: () => void;
  // onBack: () => void;
  currentStep: number;
};

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedCurriculum: TSelectOption | null;
  selectedSubject: TSelectOption | null;
};

const initialState: TState = {
  selectedCurriculum: null,
  selectedSubject: null,
};

const Step1: React.FC<Step1Props> = ({ onNext, currentStep }) => {
  const dispatch = useDispatch();

  // Store
  const { list: curriculumList, status: curriculumStatus } = useAppSelector(
    getCurriculumV2State
  );
  const { onboardingData, status: onboardingStatus } = useAppSelector(
    getOnboardingState
  );
  const onboardingUserId = getOnboardingUserIdFromLocalStorage();

  // State
  const [state, setState] = useState(initialState);
  const [isError, setIsError] = useState<boolean>(false);
  const [subjectOptions, setSubjectOptions] = useState<TSelectOption[]>([]);
  const [curriculumOptions, setCurriculumOptions] = useState<TSelectOption[]>(
    []
  );

  useEffect(() => {
    if (curriculumList && curriculumList.length) {
      setCurriculumOptions(
        curriculumList.map((item) => ({
          label: item.curriculum,
          value: item.curriculum,
        }))
      );
    }
  }, [curriculumList]);

  useEffect(() => {
    if (onboardingData?.curriculum && onboardingData?.subject) {
      setState({
        selectedCurriculum: {
          label: onboardingData.curriculum,
          value: onboardingData.curriculum,
        },
        selectedSubject: {
          label: onboardingData.subject,
          value: onboardingData.subject,
        },
      });
    }
  }, [onboardingData?.curriculum, onboardingData?.subject]);

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    if (curriculumList && curriculumList.length && value) {
      const selectedCurriculumData = curriculumList.find(
        (curriculumItem) => curriculumItem.curriculum === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        setSubjectOptions(
          selectedCurriculumData.subjects.map((item) => ({
            label: item.subject,
            value: item.subject,
          }))
        );
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: value,
      selectedSubject: null,
    }));
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedSubject: value,
    }));

    setIsError(false);
  };

  const handleContinue = async () => {
    if (state.selectedCurriculum && state.selectedSubject) {
      await dispatch(
        saveOnboarding({
          _id: onboardingUserId,
          curriculum: state.selectedCurriculum.value,
          subject: state.selectedSubject.value,
        })
      );

      onNext();
    } else {
      setIsError(true);
    }
  };

  return (
    <Grid container sx={{ height: '100vh' }}>
      {/* Left section */}
      <Grid
        item
        xs={12}
        sm={4}
        md={6}
        sx={{
          backgroundColor: '#FFF8E8',
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in>
          <img
            src={`${process.env.PUBLIC_URL}/images/onboarding/img-step1.png`}
            alt="step1"
          />
        </Fade>
      </Grid>

      {/* Right section */}
      <Grid
        item
        xs={12}
        sm={8}
        md={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Progress bar */}
        <CustomStepper activeStep={currentStep} />

        <Fade in>
          <Stack
            spacing={8}
            sx={{
              maxWidth: 375,
              width: '100%',
            }}
          >
            <Typography
              fontSize="25px"
              fontWeight="700"
              textAlign="center"
              color={TP_COLORS.gray.darken}
            >
              What subject and curriculum are you looking for?
            </Typography>

            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  fontSize="14px"
                  fontWeight="600"
                  color={TP_COLORS.gray.darken}
                >
                  Curriculum
                </Typography>
                <FormControl fullWidth sx={{ maxWidth: '220px' }}>
                  <Autocomplete
                    value={state.selectedCurriculum}
                    options={curriculumOptions}
                    onChange={handleCurriculumChange}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    loading={curriculumStatus === 'loading'}
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.value
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        placeholder="Select curriculum"
                      />
                    )}
                  />
                </FormControl>
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  fontSize="14px"
                  fontWeight="600"
                  color={TP_COLORS.gray.darken}
                >
                  Subject
                </Typography>
                <FormControl fullWidth sx={{ maxWidth: '220px' }}>
                  <Autocomplete
                    disabled={!state.selectedCurriculum}
                    value={state.selectedSubject}
                    options={subjectOptions}
                    onChange={handleSubjectChange}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    isOptionEqualToValue={(option, value) =>
                      option.label === value.value
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        placeholder="Select subject"
                      />
                    )}
                  />
                </FormControl>
              </Stack>
            </Stack>

            <Stack spacing={1}>
              <Fade in={isError}>
                <FormHelperText error style={{ marginLeft: '2px' }}>
                  Please select a curriculum and subject
                </FormHelperText>
              </Fade>
              <DarkLoadingButton
                size="large"
                sx={{ height: '55px', fontSize: '16px !important' }}
                onClick={handleContinue}
                loading={onboardingStatus === 'loading'}
              >
                Continue
              </DarkLoadingButton>
            </Stack>
          </Stack>
        </Fade>
      </Grid>
    </Grid>
  );
};

export default Step1;
